import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { getStoredMerchantStaff, hasMerchantPermission, logoutMerchant, refreshMerchantProfile } from "../../api/merchantAuthApi";

import "./app-layout.scss";

const baseMenuGroups = [
  { title: "Dashboard", items: [{ label: "Dashboard", icon: "⌂", path: "/vendor", end: true }] },
  { title: "Operations", items: [
    { label: "Today’s Availability", icon: "▤", path: "/todays-listing" },
    { label: "Pickup Queue", icon: "✓", path: "/pickup-queue" },
    { label: "Orders", icon: "▦", path: "/orders" },
  ] },
  { title: "Setup", items: [{ label: "Outlet / Products", icon: "◫", path: "/outlet-products" }] },
  { title: "Reports", items: [
    { label: "Sales Report", icon: "▥", path: "/reports/sales" },
    { label: "Order Performance", icon: "▥", path: "/reports/order-performance" },
    { label: "Listing Performance", icon: "▥", path: "/reports/listing-performance" },
  ] },
  { title: "Account", items: [
    { label: "Staff Management", icon: "♙", path: "/staff" },
    { label: "Roles & Permissions", icon: "▦", path: "/roles-permissions" },
  ] },
];


const pageTitles = {
  "/": "Dashboard",
  "/pickup-queue": "Pickup Queue",
  "/todays-listing": "Today’s Availability",
  "/orders": "Orders",
  "/outlet-products": "Outlet / Product Setting",
  "/reports": "Reports",
  "/reports/sales": "Sales Report",
  "/reports/order-performance": "Order Performance Report",
  "/reports/listing-performance": "Listing Performance Report",
  "/staff": "Staff Management",
  "/roles-permissions": "Roles & Permissions",
  "/profile": "Edit Profile",
};

const menuPermissions = {
  "/pickup-queue": "pickup_queue_view",
  "/todays-listing": "todays_availability_view",
  "/orders": "orders_view",
  "/outlet-products": "product_view",
  "/reports/sales": "reports_sales",
  "/reports/order-performance": "reports_orders",
  "/reports/listing-performance": "reports_listing",
  "/staff": "staff_view",
  "/roles-permissions": "roles_manage",
};

const resolvePageTitle = (pathname) => {
  if (pathname.startsWith("/orders/")) return "Order Detail";
  if (pathname === "/staff/create") return "Add Operator";
  if (pathname.startsWith("/staff/") && pathname.endsWith("/edit")) return "Staff Details";
  if (pathname.includes("/products/create")) return "Add Product";
  if (pathname.includes("/products/") && pathname.includes("/edit")) return "Edit Product";
  return pageTitles[pathname] || "Merchant Portal";
};

const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [outletOpen, setOutletOpen] = useState(false);
  const [, setPermissionVersion] = useState(0);
  const storedStaff = getStoredMerchantStaff();
  const outletOptions = (storedStaff?.outlets || []).map((outlet) => ({
    id: outlet.id,
    name: outlet.name,
    status: Number(outlet.status) === 1 ? "Accepting orders" : "Inactive",
  }));
  const storedOutletId = Number(localStorage.getItem("merchant_selected_outlet_id"));
  const [selectedOutlet, setSelectedOutlet] = useState(
    outletOptions.find((outlet) => outlet.id === storedOutletId) || outletOptions[0] || null
  );
  const merchantLoginCode =
    localStorage.getItem("merchant_login_code") ||
    localStorage.getItem("login_code") ||
    "MRC-4821";
  const [waitingPickupCount, setWaitingPickupCount] = useState(() => {
    const savedValue = localStorage.getItem("pickup_waiting_count");

    if (savedValue === null || savedValue === "") {
      return 3;
    }

    const savedCount = Number(savedValue);
    return Number.isFinite(savedCount) ? savedCount : 3;
  });

  useEffect(() => {
    const handleWaitingCountUpdate = (event) => {
      const nextCount = Number(event?.detail?.count);
      if (Number.isFinite(nextCount)) {
        setWaitingPickupCount(nextCount);
      }
    };

    const handleStorage = (event) => {
      if (event.key !== "pickup_waiting_count") return;
      const nextCount = Number(event.newValue);
      if (Number.isFinite(nextCount)) {
        setWaitingPickupCount(nextCount);
      }
    };

    window.addEventListener("pickup-waiting-count-updated", handleWaitingCountUpdate);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("pickup-waiting-count-updated", handleWaitingCountUpdate);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  useEffect(() => {
    const refreshPermissions = () => setPermissionVersion((current) => current + 1);
    window.addEventListener("merchant-permissions-changed", refreshPermissions);
    refreshMerchantProfile();
    return () => window.removeEventListener("merchant-permissions-changed", refreshPermissions);
  }, []);

  const menuGroups = baseMenuGroups.map((group) => ({
    ...group,
    items: group.items
      .filter((item) => !menuPermissions[item.path] || hasMerchantPermission(menuPermissions[item.path]))
      .map((item) => item.path === "/pickup-queue"
        ? { ...item, badge: waitingPickupCount }
        : item),
  })).filter((group) => group.items.length);

  const handleLogout = async () => {
    await logoutMerchant();
    navigate("/login");
  };

  return (
    <div className="app-layout">
      <aside className={`app-layout__sidebar ${sidebarOpen ? "app-layout__sidebar--open" : ""}`}>
        <div className="app-layout__brand">
          <div className="app-layout__brand-mark">SB</div>
          <div>
            <div className="app-layout__brand-name">SecondBite</div>
            <div className="app-layout__brand-subtitle">Merchant Portal</div>
          </div>
        </div>

        <div className="app-layout__outlet-switcher">
          <div className="app-layout__outlet-label">Outlet</div>
          <button
            className={`app-layout__outlet-button ${outletOpen ? "app-layout__outlet-button--open" : ""}`}
            type="button"
            onClick={() => setOutletOpen((open) => !open)}
          >
            <span className="app-layout__outlet-name">{selectedOutlet?.name || "No outlet"}</span>
            <span className="app-layout__outlet-chevron">⌄</span>
          </button>

          {outletOpen && (
            <div className="app-layout__outlet-menu" role="menu">
              {outletOptions.map((outlet) => {
                const isSelected = outlet.id === selectedOutlet?.id;

                return (
                  <button
                    key={outlet.id}
                    className={`app-layout__outlet-option ${isSelected ? "app-layout__outlet-option--active" : ""}`}
                    type="button"
                    onClick={() => {
                      setSelectedOutlet(outlet);
                      localStorage.setItem("merchant_selected_outlet_id", String(outlet.id));
                      window.dispatchEvent(new CustomEvent("merchant-outlet-changed", {
                        detail: { outletId: outlet.id },
                      }));
                      setOutletOpen(false);
                      setSidebarOpen(false);
                    }}
                  >
                    <span className="app-layout__outlet-option-copy">
                      <span className="app-layout__outlet-option-name">{outlet.name}</span>
                      <span className="app-layout__outlet-option-status">{outlet.status}</span>
                    </span>
                    {isSelected && <span className="app-layout__outlet-option-check">✓</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <nav className="app-layout__menu">
          {menuGroups.map((group) => (
            <div className="app-layout__menu-group" key={group.title}>
              <div className="app-layout__menu-title">{group.title}</div>
              {group.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  className={({ isActive }) =>
                    `app-layout__menu-item ${isActive ? "app-layout__menu-item--active" : ""}`
                  }
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="app-layout__menu-icon">{item.icon}</span>
                  <span className="app-layout__menu-label">{item.label}</span>
                  {item.badge !== undefined ? (
                    <span
                      className="app-layout__menu-badge"
                      aria-label={`${item.badge} waiting pickup orders`}
                    >
                      {item.badge}
                    </span>
                  ) : null}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="app-layout__mobile-logout">
          <button className="app-layout__logout" type="button" onClick={handleLogout}>
            <span>↩</span>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {sidebarOpen && <button className="app-layout__overlay" type="button" aria-label="Close menu" onClick={() => setSidebarOpen(false)} />}

      <main className="app-layout__main">
        <header className="app-layout__topbar">
            <button className="app-layout__menu-toggle" type="button" aria-label="Open menu" onClick={() => setSidebarOpen(true)}>☰</button>
            <div>
              <p className="app-layout__eyebrow">MVP B2B Platform</p>
              <h1 className="app-layout__title">{resolvePageTitle(location.pathname)}</h1>
            </div>

            <div className="app-layout__topbar-account">
              <div className="app-layout__login-code" aria-label={`Merchant login code ${merchantLoginCode}`}>
                <span className="app-layout__login-code-label">Login Code</span>
                <strong className="app-layout__login-code-value">{merchantLoginCode}</strong>
              </div>

              <div className="app-layout__profile-wrap">
            <button className={`app-layout__profile ${profileOpen ? "app-layout__profile--open" : ""}`} type="button" onClick={() => setProfileOpen((open) => !open)}>
              <span className="app-layout__avatar">M</span>
              <span>Merchant</span>
              <span className="app-layout__profile-chevron">⌄</span>
            </button>
            {profileOpen && (
              <>
                <button className="app-layout__profile-backdrop" type="button" aria-label="Close profile menu" onClick={() => setProfileOpen(false)} />
                <div className="app-layout__profile-menu" role="menu">
                  <button className="app-layout__profile-menu-item" type="button" onClick={() => { setProfileOpen(false); navigate("/profile"); }}>
                    <span className="app-layout__profile-menu-icon">♙</span><span>Edit Profile</span>
                  </button>
                  <button className="app-layout__profile-menu-item app-layout__profile-menu-item--danger" type="button" onClick={handleLogout}>
                    <span className="app-layout__profile-menu-icon">↩</span><span>Logout</span>
                  </button>
                </div>
              </>
            )}
              </div>
            </div>
        </header>
        <section className="app-layout__content">
          <Outlet context={{ selectedOutlet, outletOptions }} />
        </section>
      </main>
    </div>
  );
};

export default AppLayout;
